'use client';

import { DownloadIcon, ExternalLinkIcon, FolderOpenIcon, PackageIcon, RotateCwIcon, ServerIcon, Trash2Icon, XIcon } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ManageContentRegistriesDialog } from './ManageContentRegistriesDialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Progress } from './ui/progress';
import { ScrollArea } from './ui/scroll-area';
import { Spinner } from './ui/spinner';
import { Toggle } from './ui/toggle';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { type AssetPackInstallResult, type GenesysContentMetadata, sdk } from '@/app/sdk';
import { buildProject } from '@/editor/core/actions/editorActions';
import { backgroundTaskStore } from '@/editor/store/backgroundTaskStore';
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** Narrows {@link GenesysContentMetadata} to the asset-pack variant. */
type AssetPackContentMetadata = Extract<GenesysContentMetadata, {
  type: 'asset-pack';
}>;
type CompatibilityBadgeInfo = {
  label: string;
  variant: 'destructive' | 'secondary' | 'outline';
  tooltip?: string;
};
const getCompatibilityBadge = (pack: AssetPackContentMetadata): CompatibilityBadgeInfo | undefined => {
  const status = pack.compatibility?.status;
  if (status === 'incompatible-older') {
    return {
      label: pack.minEngineVersion ? `Requires engine ≥ ${pack.minEngineVersion}` : 'Incompatible',
      variant: 'destructive',
      tooltip: pack.compatibility?.message
    };
  }
  if (status === 'compatible-newer') {
    return {
      label: 'Older Version',
      variant: 'secondary',
      tooltip: pack.compatibility?.message
    };
  }
  return undefined;
};
const isPackBlocked = (pack: AssetPackContentMetadata | undefined): boolean => pack?.compatibility?.status === 'incompatible-older';

/**
 * Whether the pack has a deletable cached copy in the user-managed local
 * content folder.  Built-in packs ship with the SDK and cannot be removed
 * through this dialog.
 */
const canDeleteFromCache = (pack: AssetPackContentMetadata | undefined): boolean => !!pack && pack.availability.installable && pack.sourceKind !== 'built-in';

/** Pack has no local copy yet but a remote registry advertises it. */
const canDownload = (pack: AssetPackContentMetadata | undefined): boolean => !!pack && pack.availability.downloadable && !pack.availability.cachedVersion;

/** Pack has a local copy and the remote advertises a different version. */
const canUpdate = (pack: AssetPackContentMetadata | undefined): boolean => !!pack && pack.availability.downloadable && !!pack.availability.cachedVersion && !!pack.availability.remoteVersion && pack.availability.cachedVersion !== pack.availability.remoteVersion;

/**
 * Summary badge derived from `availability` + `installedInProject`.  Stacked
 * with the compatibility badge so both can show at once.  Resolution order
 * (most specific first): Installed → Built-in → Update → Cached → Available.
 */
const getAvailabilityBadge = (pack: AssetPackContentMetadata): CompatibilityBadgeInfo | undefined => {
  if (pack.installedInProject) {
    return {
      label: `Installed v${pack.installedInProject.version}`,
      variant: 'secondary'
    };
  }
  if (pack.sourceKind === 'built-in') {
    return {
      label: 'Built-in',
      variant: 'outline'
    };
  }
  if (pack.availability.installable && pack.availability.downloadable && pack.availability.cachedVersion && pack.availability.remoteVersion && pack.availability.cachedVersion !== pack.availability.remoteVersion) {
    return {
      label: `Update v${pack.availability.remoteVersion}`,
      variant: 'secondary'
    };
  }
  if (pack.availability.installable) return {
    label: 'Cached',
    variant: 'outline'
  };
  if (pack.availability.downloadable) return {
    label: 'Available',
    variant: 'outline'
  };
  return undefined;
};
interface BrowseAssetPacksDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absolute path to the currently-open project folder. */
  targetProjectPath: string;
}
export const BrowseAssetPacksDialog = ({
  open,
  onOpenChange,
  targetProjectPath
}: BrowseAssetPacksDialogProps) => {
  const [packs, setPacks] = useState<AssetPackContentMetadata[]>([]);
  const [selectedPackId, setSelectedPackId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInstalling, setIsInstalling] = useState(false);
  const [pendingConflict, setPendingConflict] = useState<{
    packId: string;
    conflictingFolder: string;
  } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{
    packId: string;
    packName: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<{
    bytesTransferred: number;
    totalBytes?: number;
  } | null>(null);
  const [manageRegistriesOpen, setManageRegistriesOpen] = useState(false);
  const selectedPack = useMemo(() => packs.find(p => p.id === selectedPackId), [packs, selectedPackId]);
  const fetchPacks = useCallback(async () => {
    if (!sdk?.tools.listContent) {
      console.warn('SDK listContent not available');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fetched = await sdk.tools.listContent({
        type: 'asset-pack',
        targetProjectPath
      });
      setPacks(fetched as AssetPackContentMetadata[]);
      if (fetched.length > 0 && !selectedPackId) {
        setSelectedPackId(fetched[0].id);
      }
    } catch (err) {
      console.error('Failed to load asset packs:', err);
      setError('Failed to load asset packs');
    } finally {
      setLoading(false);
    }
  }, [selectedPackId, targetProjectPath]);
  useEffect(() => {
    if (open && packs.length === 0) {
      void fetchPacks();
    }
  }, [open, packs.length, fetchPacks]);
  const handleOpenLocalFolder = useCallback(async () => {
    try {
      await sdk?.tools.openLocalContentFolder();
    } catch (err_0) {
      console.error('Failed to open local asset packs folder:', err_0);
    }
  }, []);
  const reportInstallResult = useCallback((res: AssetPackInstallResult, pack: AssetPackContentMetadata) => {
    if (res.success) {
      const folderName = res.folderPath.split(/[/\\]/).at(-1) ?? pack.name;
      console.log(`[asset-packs] Installed "${pack.name}" at ${res.folderPath}`);
      toast.success(`Installed ${pack.name} into packs/${folderName}/`);
    } else {
      console.error(`[asset-packs] Failed to install "${pack.name}":`, res.message, res.error);
    }
  }, []);
  const performInstall = useCallback(async (packId: string, force: boolean): Promise<AssetPackInstallResult | undefined> => {
    if (!sdk?.tools.installAssetPack) {
      return undefined;
    }
    return sdk.tools.installAssetPack(packId, targetProjectPath, force ? {
      force: true
    } : undefined);
  }, [targetProjectPath]);
  const handleInstall = useCallback(async () => {
    if (!selectedPack || !targetProjectPath) return;
    setIsInstalling(true);
    try {
      const res = await performInstall(selectedPack.id, false);
      if (!res) return;
      if (!res.success && res.reason === 'folder-exists' && res.conflictingFolder) {
        setPendingConflict({
          packId: selectedPack.id,
          conflictingFolder: res.conflictingFolder
        });
        return;
      }
      reportInstallResult(res, selectedPack);
      if (res.success) {
        onOpenChange(false);
        await buildProject();
      }
    } finally {
      setIsInstalling(false);
    }
  }, [selectedPack, targetProjectPath, performInstall, reportInstallResult, onOpenChange]);
  const handleConfirmOverwrite = useCallback(async () => {
    if (!pendingConflict || !selectedPack) return;
    setIsInstalling(true);
    try {
      const res = await performInstall(pendingConflict.packId, true);
      setPendingConflict(null);
      if (!res) return;
      reportInstallResult(res, selectedPack);
      if (res.success) {
        onOpenChange(false);
        await buildProject();
      }
    } finally {
      setIsInstalling(false);
    }
  }, [pendingConflict, selectedPack, performInstall, reportInstallResult, onOpenChange]);
  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    if (!sdk?.tools.deleteLocalContent) {
      setPendingDelete(null);
      return;
    }
    setIsDeleting(true);
    try {
      const res = await sdk.tools.deleteLocalContent(pendingDelete.packId);
      if (res.success) {
        toast.success(`Removed cached copy of ${pendingDelete.packName}`);
        // Drop selection if the deleted pack was selected and is no longer downloadable.
        const target = packs.find(p => p.id === pendingDelete.packId);
        if (target && !target.availability.downloadable && selectedPackId === pendingDelete.packId) {
          setSelectedPackId('');
        }
        await fetchPacks();
      } else {
        toast.error(`Failed to delete ${pendingDelete.packName}: ${res.message}`);
      }
    } finally {
      setPendingDelete(null);
      setIsDeleting(false);
    }
  }, [pendingDelete, packs, selectedPackId, fetchPacks]);
  const handleDownload = useCallback(async () => {
    if (!selectedPack || !sdk?.tools.downloadContent) return;
    setIsDownloading(true);
    setDownloadProgress(null);
    backgroundTaskStore.addTask({
      id: selectedPack.id,
      label: `Downloading ${selectedPack.name}`
    });
    const unsubscribe = sdk.tools.onDownloadProgress?.(event => {
      if (event.id === selectedPack.id) {
        const transferred = event.bytesTransferred;
        const total = event.totalBytes;
        const progress = total ? Math.round(transferred / total * 100) : undefined;
        const detail = total ? `${formatBytes(transferred)} / ${formatBytes(total)}` : formatBytes(transferred);
        setDownloadProgress({
          bytesTransferred: transferred,
          totalBytes: total
        });
        backgroundTaskStore.updateTask(selectedPack.id, {
          progress,
          detail
        });
      }
    });
    try {
      const res = await sdk.tools.downloadContent(selectedPack.id);
      if (res.success) {
        toast.success(`Downloaded ${selectedPack.name}`);
        await fetchPacks();
      } else if (!res.cancelled) {
        toast.error(`Failed to download ${selectedPack.name}: ${res.message}`);
      }
    } finally {
      unsubscribe?.();
      backgroundTaskStore.removeTask(selectedPack.id);
      setIsDownloading(false);
      setDownloadProgress(null);
    }
  }, [selectedPack, fetchPacks]);
  const handleCancelDownload = useCallback(() => {
    if (selectedPack) {
      void sdk?.tools.cancelDownload?.(selectedPack.id);
    }
  }, [selectedPack]);
  const isSelectedBlocked = isPackBlocked(selectedPack);
  const isInstallDisabled = !selectedPackId || !targetProjectPath || isSelectedBlocked || isInstalling || !selectedPack?.availability.installable;
  const showDownloadButton = canDownload(selectedPack);
  const showUpdateButton = canUpdate(selectedPack);
  return <>
      <Dialog open={open && !isInstalling} onOpenChange={onOpenChange}>
        <DialogContent className="min-w-4xl max-w-4xl max-h-screen" overlayExcludeTitleBar={false}>
          <DialogHeader>
            <DialogTitle>Browse Asset Packs</DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              Install assets, prefabs, and scripts into the current project.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-3">
            {loading ? <div className="flex items-center justify-center p-8">
                <Spinner size="md" />
                <span className="ml-2">Loading asset packs...</span>
              </div> : error ? <div className="text-center p-4 text-destructive">
                <p>{error}</p>
                <Button variant="default" size="sm" onClick={fetchPacks} className="mt-2">Retry</Button>
              </div> : packs.length === 0 ? <div className="flex flex-col items-center justify-center gap-3 p-8 text-muted-foreground">
                <PackageIcon className="h-8 w-8" />
                <p className="text-sm">No asset packs available.</p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={fetchPacks} disabled={loading} className="text-xs">
                    <RotateCwIcon className="h-3.5 w-3.5" />
                    Reload from disk
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleOpenLocalFolder} className="text-xs">
                    <FolderOpenIcon className="h-3.5 w-3.5" />
                    Open local asset packs folder
                  </Button>
                </div>
              </div> : <div className="flex gap-3 h-[360px] rounded-md border bg-card">
                <ScrollArea className="flex-1 border-r">
                  <div className="grid grid-cols-3 gap-2 p-2">
                    {packs.map(pack_0 => {
                  const badge = getCompatibilityBadge(pack_0);
                  const availBadge = getAvailabilityBadge(pack_0);
                  const blocked = isPackBlocked(pack_0);
                  const toggle = <Toggle pressed={selectedPackId === pack_0.id} onPressedChange={pressed => pressed && setSelectedPackId(pack_0.id)} variant="outline" className={`relative flex flex-col items-center justify-center gap-2 p-2 h-[110px] w-full data-[state=on]:ring-2 data-[state=on]:ring-ring${blocked ? ' opacity-60' : ''}`}>
                          <div className="h-12 w-full rounded-sm bg-muted flex items-center justify-center">
                            {pack_0.thumbnailUrl ?
                      // eslint-disable-next-line @next/next/no-img-element
                      <img src={pack_0.thumbnailUrl} alt={pack_0.name} className="h-full w-full object-cover rounded-sm" /> : <PackageIcon className="h-5 w-5 text-muted-foreground" />}
                          </div>
                          <div className="text-xs font-medium text-center leading-tight truncate w-full">{pack_0.name}</div>
                          {pack_0.sizeBytes !== undefined && <div className="text-[10px] text-muted-foreground leading-none">{formatBytes(pack_0.sizeBytes)}</div>}
                          {badge && <Badge variant={badge.variant} className="absolute top-1 right-1 px-1.5 py-0 text-[10px] leading-tight pointer-events-none">
                              {badge.label}
                            </Badge>}
                          {availBadge && !badge && <Badge variant={availBadge.variant} className="absolute top-1 left-1 px-1.5 py-0 text-[10px] leading-tight pointer-events-none">
                              {availBadge.label}
                            </Badge>}
                        </Toggle>;
                  // Wrap in a relative container so the delete overlay can be a sibling
                  // of the Toggle (which renders as <button>) — nested <button> is invalid HTML.
                  const card = <div key={pack_0.id} className="relative group">
                          {toggle}
                          {canDeleteFromCache(pack_0) && <button type="button" aria-label="Delete from cache" onClick={() => setPendingDelete({
                      packId: pack_0.id,
                      packName: pack_0.name
                    })} className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity rounded p-0.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10">
                              <Trash2Icon className="h-3 w-3" />
                            </button>}
                        </div>;
                  if (!badge?.tooltip) return card;
                  return <Tooltip key={pack_0.id}>
                          <TooltipTrigger asChild>{card}</TooltipTrigger>
                          <TooltipContent>{badge.tooltip}</TooltipContent>
                        </Tooltip>;
                })}
                  </div>
                </ScrollArea>

                <ScrollArea className="w-80">
                  {selectedPack ? <div className="p-3 space-y-3">
                      <div className="w-full aspect-video rounded-md overflow-hidden flex items-center justify-center bg-muted">
                        {selectedPack.thumbnailUrl ?
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={selectedPack.thumbnailUrl} alt={selectedPack.name} className="w-full h-full object-cover" /> : <PackageIcon className="h-10 w-10 text-muted-foreground" />}
                      </div>
                      {selectedPack.licenseSummary && <p className="text-[11px] text-muted-foreground">License: {selectedPack.licenseSummary}</p>}
                      <div className="space-y-1">
                        <p className="text-sm font-semibold text-foreground">{selectedPack.name}</p>
                        <p className="text-xs text-muted-foreground">{selectedPack.description}</p>
                      </div>
                      {selectedPack.version && <p className="text-[11px] text-muted-foreground">Version: {selectedPack.version}</p>}
                      {selectedPack.sizeBytes !== undefined && <p className="text-[11px] text-muted-foreground">Size: {formatBytes(selectedPack.sizeBytes)}</p>}
                      {selectedPack.registry && <p className="text-[11px] text-muted-foreground">Registry: {selectedPack.registry.label}</p>}
                      {selectedPack.contents && <p className="text-[11px] text-muted-foreground">
                          Contents:{' '}
                          {[selectedPack.contents.assets ? `${selectedPack.contents.assets} assets` : undefined, selectedPack.contents.prefabs ? `${selectedPack.contents.prefabs} prefabs` : undefined, selectedPack.contents.scripts ? `${selectedPack.contents.scripts} scripts` : undefined].filter(Boolean).join(', ')}
                        </p>}
                      {(() => {
                  const compat = getCompatibilityBadge(selectedPack);
                  const avail = getAvailabilityBadge(selectedPack);
                  if (!compat && !avail) return null;
                  return <div className="space-y-1 flex flex-col items-start gap-1">
                            {avail && <Badge variant={avail.variant}>{avail.label}</Badge>}
                            {compat && <Badge variant={compat.variant}>{compat.label}</Badge>}
                            {compat?.tooltip && <p className="text-[11px] text-muted-foreground leading-snug">{compat.tooltip}</p>}
                          </div>;
                })()}

                    </div> : <div className="p-4 text-sm text-muted-foreground text-center">Select an asset pack to see details</div>}
                </ScrollArea>
              </div>}
            {!loading && !error && packs.length > 0 && <div className="flex justify-end gap-2">
                <Button type="button" variant="ghost" size="sm" onClick={fetchPacks} disabled={loading} className="text-xs text-muted-foreground hover:text-foreground">
                  <RotateCwIcon className="h-3.5 w-3.5" />
                  Reload from disk
                </Button>
                <Button type="button" variant="ghost" size="sm" onClick={handleOpenLocalFolder} className="text-xs text-muted-foreground hover:text-foreground">
                  <FolderOpenIcon className="h-3.5 w-3.5" />
                  Open local asset packs folder
                  <ExternalLinkIcon className="h-3 w-3" />
                </Button>
              </div>}
          </div>

          {isDownloading && <div className="space-y-1 px-1">
              <div className="flex items-center gap-2">
                <Progress className="flex-1" value={downloadProgress?.totalBytes ? Math.round(downloadProgress.bytesTransferred / downloadProgress.totalBytes * 100) : undefined} />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={handleCancelDownload}>
                      <XIcon className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Cancel download</TooltipContent>
                </Tooltip>
              </div>
              <p className="text-xs text-muted-foreground text-right">
                {downloadProgress ? `${formatBytes(downloadProgress.bytesTransferred)}${downloadProgress.totalBytes ? ` / ${formatBytes(downloadProgress.totalBytes)}` : ''}` : 'Connecting…'}
              </p>
            </div>}

          <DialogFooter>
            {/* Left-pinned utility button: always reachable, including from the empty/loading/error states. */}
            <Button type="button" variant="ghost" size="sm" onClick={() => setManageRegistriesOpen(true)} className="text-xs text-muted-foreground hover:text-foreground mr-auto">
              <ServerIcon className="h-3.5 w-3.5" />
              Manage Registries
            </Button>
            {isDownloading ? <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="default" onClick={() => onOpenChange(false)}>Close</Button>
                </TooltipTrigger>
                <TooltipContent>Download continues in the background</TooltipContent>
              </Tooltip> : <Button variant="default" onClick={() => onOpenChange(false)}>Close</Button>}
            {(showDownloadButton || showUpdateButton) && <Button variant="outline" onClick={handleDownload} disabled={isDownloading}>
                {isDownloading ? <Spinner size="sm" /> : <DownloadIcon className="h-3.5 w-3.5" />}
                {showUpdateButton ? `Update to ${selectedPack?.availability.remoteVersion}` : 'Download'}
              </Button>}
            <Button onClick={handleInstall} disabled={isInstallDisabled} tooltip={isSelectedBlocked ? selectedPack?.compatibility?.message ?? 'This asset pack is incompatible with the project.' : !selectedPack?.availability.installable ? 'Download this asset pack before installing.' : undefined}>
              Install
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={pendingConflict !== null} onOpenChange={(o: boolean) => !o && setPendingConflict(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Overwrite existing pack folder?</AlertDialogTitle>
            <AlertDialogDescription>
              The destination folder for this asset pack already exists. Continuing will delete its current contents and replace them with the pack.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pendingConflict && <div className="rounded-md border bg-muted/30 p-2 text-xs font-mono break-all">
              {pendingConflict.conflictingFolder}
            </div>}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={() => {
            void handleConfirmOverwrite();
          }}>
              Overwrite
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pendingDelete !== null} onOpenChange={(o_0: boolean) => !o_0 && !isDeleting && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete cached asset pack?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the cached copy of <span className="font-semibold">{pendingDelete?.packName}</span> from your local content folder. Asset packs already installed into projects are not affected. You can re-download the pack later if a remote registry still advertises it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" disabled={isDeleting} onClick={() => {
            void handleConfirmDelete();
          }}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ManageContentRegistriesDialog open={manageRegistriesOpen} onOpenChange={setManageRegistriesOpen} onRegistriesChanged={() => {
      void fetchPacks();
    }} />
    </>;
};
BrowseAssetPacksDialog.displayName = 'BrowseAssetPacksDialog';